import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";

// === THEMES AMÉLIORÉS ===
const themes = {
  light: {
    sidebarBg: "#f6f8fa",
    color: "#21253a",
    active: "#2563eb",
    accent: "#fa709a",
    border: "1.5px solid #e5e7eb",
    navHover: "#eceff1",
    avatarRing: "#fee140",
    menuBg: "#fff",
  },
  dark: {
    sidebarBg: "#1a1d2b",
    color: "#f3f4f6",
    active: "#fee140",
    accent: "#fa709a",
    border: "1.5px solid #23253a",
    navHover: "#23253a",
    avatarRing: "#fa709a",
    menuBg: "#23253a",
  }
};

const getInitials = (prenom, nom) => {
  if (!prenom && !nom) return "?";
  return (
    (prenom ? prenom[0] : "") +
    (nom ? nom[0] : "")
  ).toUpperCase();
};

const avatarColors = ["#fa709a", "#fee140", "#5ac8fa", "#2563eb", "#22c55e"];

const ClientSidebar = ({ themeKey = "light", onToggleTheme }) => {
  const [user, setUser] = useState(null);
  const [avatarMenu, setAvatarMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 992);
  const history = useHistory();

  const theme = themes[themeKey];

  // --- USER API FETCH ---
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) return;
        const response = await axios.get("https://appapi.unpaidfinancial.com/api/me", {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        setUser(response.data);
      } catch (e) {}
    };
    fetchUser();
  }, []);

  // --- RESPONSIVE GESTION SIDEBAR ---
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- LOGOUT ---
  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/me/logout";
  };

  const isActive = (path) => history.location.pathname.includes(path);

  // --- DYNAMIC AVATAR COLOR ---
  let bgColor = user
    ? avatarColors[
        ((user.nom?.charCodeAt(0) || 0) + (user.prenom?.charCodeAt(0) || 0)) %
          avatarColors.length
      ]
    : "#bcbcbc";

  // --- STYLES ---
  const styles = {
    sidebar: {
      width: 285,
      height: "100vh",
      position: "fixed",
      left: 0,
      top: 0,
      zIndex: 1300,
      background: theme.sidebarBg,
      color: theme.color,
      borderRight: theme.border,
      padding: "0 0 2.5rem 0",
      boxShadow: "2px 0 18px rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      backdropFilter: "blur(8px)",
      transition: "transform 0.3s",
      willChange: "transform"
    },
    logo: {
      textDecoration: "none",
      fontWeight: 700,
      fontSize: "1.30rem",
      letterSpacing: "1.1px",
      color: theme.active,
      marginLeft: "1.8rem"
    },
    avatarBlock: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "36px 0 15px 0",
      background: "transparent",
      position: "relative"
    },
    avatar: {
      width: 72,
      height: 72,
      borderRadius: "50%",
      background: bgColor,
      color: "#fff",
      fontWeight: 700,
      fontSize: "2.1rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 3px 13px rgba(100,100,100,0.13)",
      border: `3.5px solid ${theme.avatarRing}`,
      cursor: "pointer",
      position: "relative",
      transition: "box-shadow 0.18s"
    },
    avatarName: {
      marginTop: 11,
      fontWeight: 600,
      fontSize: 18,
      color: theme.accent,
      letterSpacing: ".2px",
      textAlign: "center"
    },
    avatarEmail: {
      fontSize: 13,
      color: themeKey === "dark" ? "#ccc" : "#8a8a8a",
      marginTop: 2,
      marginBottom: 5,
      textAlign: "center"
    },
    avatarMenu: {
      position: "absolute",
      top: 80,
      left: 50,
      background: theme.menuBg,
      minWidth: "220px",
      borderRadius: "13px",
      boxShadow: "0 10px 32px rgba(80,60,120,0.15)",
      border: "1px solid #eee",
      padding: "0.6rem 0",
      zIndex: 10,
      animation: "fade-in 0.25s",
      color: themeKey === "dark" ? "#f6f6f7" : "#23264e"
    },
    menuItem: {
      padding: "11px 20px",
      fontWeight: 500,
      fontSize: 15,
      cursor: "pointer",
      transition: "background 0.14s",
      color: "inherit"
    },
    menuItemDanger: {
      color: "#ff4b4b"
    },
    navLink: {
      transition: "all 0.23s",
      borderRadius: "7px",
      margin: "3px 0",
      padding: "0.63rem 1.6rem",
      color: theme.color,
      textDecoration: "none",
      display: "flex",
      alignItems: "center",
      fontWeight: 500,
      fontSize: 16,
      gap: "12px"
    },
    activeLink: {
      fontWeight: 700,
      background: theme.active + "1A",
      color: theme.active,
      borderLeft: `4.5px solid ${theme.accent}`,
      marginLeft: "-8px"
    },
    footer: {
      margin: "2rem 0 0 0",
      textAlign: "center",
      fontSize: 13,
      color: theme.color,
      opacity: 0.38
    },
    themeBtn: {
      marginTop: 16,
      borderRadius: "7px",
      fontWeight: 500,
      background: theme.active + "18",
      color: theme.active,
      border: "none",
      padding: "6px 15px",
      cursor: "pointer",
      fontSize: 15
    }
  };

  // --- RENDER ---
  return (
    <>
      {/* SIDEBAR (desktop) */}
      <div
        style={{
          ...styles.sidebar,
          ...(sidebarOpen && window.innerWidth >= 992
            ? { transform: "translateX(0)" }
            : { transform: "translateX(-100%)" }),
          transition: "transform 0.3s cubic-bezier(.7,0,.3,1)"
        }}
        className="sidebar"
      >
        <div>
          {/* LOGO */}
          <Link to="/" style={styles.logo}>
            <i className="fas fa-bolt me-2"></i> AidFoundation
          </Link>
          {/* AVATAR + INFOS */}
          <div style={styles.avatarBlock}>
            <div
              style={styles.avatar}
              onClick={() => setAvatarMenu(!avatarMenu)}
              title={user ? `${user.prenom} ${user.nom}` : ""}
            >
              {user ? getInitials(user.prenom, user.nom) : "?"}
            </div>
            <div style={styles.avatarName}>
              {user ? `${user.prenom} ${user.nom}` : "Chargement…"}
            </div>
            {user?.email && (
              <div style={styles.avatarEmail}>{user.email}</div>
            )}

           

            {/* Menu AVATAR */}
            {avatarMenu && (
              <div style={styles.avatarMenu}>
                <div
                  style={styles.menuItem}
                  onClick={() => {
                    setAvatarMenu(false);
                    history.push("/me");
                  }}
                >
                  <i className="fas fa-user-circle me-2"></i> Mon profil
                </div>
                <div
                  style={styles.menuItem}
                  onClick={() => {
                    setAvatarMenu(false);
                    history.push("/me/modifier");
                  }}
                >
                  <i className="fas fa-edit me-2"></i> Modifier ma demande
                </div>
                <div
                  style={styles.menuItem}
                  onClick={() => {
                    setAvatarMenu(false);
                    onToggleTheme && onToggleTheme();
                  }}
                >
                  <i className="fas fa-adjust me-2"></i>
                  {themeKey === "light" ? "Thème sombre" : "Thème clair"}
                </div>
                <div
                  style={{ ...styles.menuItem, ...styles.menuItemDanger }}
                  onClick={handleLogout}
                >
                  <i className="fas fa-sign-out-alt me-2"></i> Déconnexion
                </div>
              </div>
            )}
          </div>
          <hr />
        </div>
        {/* NAVIGATION */}
        <ul className="nav flex-column mb-auto" style={{ paddingLeft: 0, marginTop: "0.4rem" }}>
          <li>
            <Link
              to="/me/statut"
              style={{
                ...styles.navLink,
                ...(isActive("/me/statut") && styles.activeLink)
              }}
              onClick={() => setSidebarOpen(false)}
            >
              <i className="fas fa-clipboard-list"></i>
              Statut
            </Link>
          </li>
          <li>
            <Link
              to="/me"
              style={{
                ...styles.navLink,
                ...(isActive("/me") && styles.activeLink)
              }}
              onClick={() => setSidebarOpen(false)}
            >
              <i className="fas fa-user"></i>
              Mon profil
            </Link>
          </li>
          <li>
            <Link
              to="/me/modifier"
              style={{
                ...styles.navLink,
                ...(isActive("/me/modifier") && styles.activeLink)
              }}
              onClick={() => setSidebarOpen(false)}
            >
              <i className="fas fa-edit"></i>
              Modifier ma demande
            </Link>
          </li>
          <li>
            <Link
              to="/me/contactus"
              style={{
                ...styles.navLink,
                ...(isActive("/me/contactus") && styles.activeLink)
              }}
              onClick={() => setSidebarOpen(false)}
            >
              <i className="fas fa-envelope"></i>
              Contactez-nous
            </Link>
          </li>
        </ul>
        {/* FOOTER + THEME */}
        <div style={styles.footer}>
          <button
            style={styles.themeBtn}
            onClick={onToggleTheme}
          >
            <i className="fas fa-adjust me-2"></i>
            Changer de thème
          </button>
          <br />
          <i className="fas fa-leaf me-2"></i>
          Powered by AidFoundation
        </div>
      </div>

      {/* OVERLAY pour mobile */}
      {window.innerWidth < 992 && sidebarOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            backgroundColor: "rgba(0,0,0,0.33)"
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* BARRE FLOTTANTE EN BAS SUR MOBILE */}
      {window.innerWidth < 992 && (
        <nav
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1400,
            background: theme.sidebarBg,
            borderTop: theme.border,
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            padding: "0.6rem 0",
            boxShadow: "0 -2px 10px rgba(0,0,0,0.09)",
          }}
        >
          <Link to="/me/statut" style={{ textAlign: "center", color: isActive("/me/statut") ? theme.active : theme.color, textDecoration: "none" }}>
            <i className="fas fa-clipboard-list" style={{ fontSize: 20 }}></i>
            <div style={{ fontSize: 12 }}>Statut</div>
          </Link>
          <Link to="/me" style={{ textAlign: "center", color: isActive("/me") ? theme.active : theme.color, textDecoration: "none" }}>
            <i className="fas fa-user" style={{ fontSize: 20 }}></i>
            <div style={{ fontSize: 12 }}>Profil</div>
          </Link>
          <Link to="/me/modifier" style={{ textAlign: "center", color: isActive("/me/modifier") ? theme.active : theme.color, textDecoration: "none" }}>
            <i className="fas fa-edit" style={{ fontSize: 20 }}></i>
            <div style={{ fontSize: 12 }}>Modifier</div>
          </Link>
          <Link to="/me/contactus" style={{ textAlign: "center", color: isActive("/me/contactus") ? theme.active : theme.color, textDecoration: "none" }}>
            <i className="fas fa-envelope" style={{ fontSize: 20 }}></i>
            <div style={{ fontSize: 12 }}>Contact</div>
          </Link>
          <button
            onClick={handleLogout}
            style={{
              background: "none",
              border: "none",
              textAlign: "center",
              color: "#ff4b4b",
              cursor: "pointer",
              fontSize: 18,
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            <i className="fas fa-sign-out-alt" style={{ fontSize: 20 }}></i>
            <div style={{ fontSize: 12 }}>Déconnexion</div>
          </button>
        </nav>
      )}

      {/* CSS responsive/animation */}
      <style>
        {`
        @media (min-width: 992px) {
          .sidebar {
            transform: translateX(0) !important;
          }
          .d-lg-none {
            display: none !important;
          }
        }
        .nav-link:hover:not(.active) {
          background-color: ${theme.navHover} !important;
          color: ${theme.accent} !important;
          transform: translateX(5px) scale(1.05);
        }
        @media (max-width: 991.98px) {
          .sidebar {
            width: 85vw !important;
            display: none !important;
          }
        }
        @media (max-width: 575.98px) {
          .sidebar {
            width: 98vw !important;
            display: none !important;
          }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-20px);}
          to { opacity: 1; transform: none;}
        }
        `}
      </style>
    </>
  );
};

export default ClientSidebar;
